import type {
  U3141AnnotatedLotChainIndexMergeDryRunLot,
  U3141AnnotatedLotChainIndexMergeDryRunLotStatus,
  U3141AnnotatedLotChainIndexMergeDryRunResponse,
  U3141AnnotatedLotChainIndexMergeDryRunSummary,
} from '../U3141Service';

const DRY_RUN_LOT_STATUSES: U3141AnnotatedLotChainIndexMergeDryRunLotStatus[] = [
  'ok',
  'missing_index',
  'will_be_masked',
  'anchor_not_visible',
];

function isDryRunLotStatus(value: string): value is U3141AnnotatedLotChainIndexMergeDryRunLotStatus {
  return (DRY_RUN_LOT_STATUSES as string[]).includes(value);
}

function parseSummary(raw: unknown): U3141AnnotatedLotChainIndexMergeDryRunSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (
    typeof row.readyCount !== 'number' ||
    typeof row.missingIndexCount !== 'number' ||
    typeof row.maskedCount !== 'number' ||
    typeof row.mergeLotCount !== 'number' ||
    typeof row.canLaunchJob !== 'boolean'
  ) {
    return null;
  }
  const blockReason = row.blockReason;
  const annotationsNewerThanChainCount = row.annotationsNewerThanChainCount;
  return {
    readyCount: row.readyCount,
    missingIndexCount: row.missingIndexCount,
    maskedCount: row.maskedCount,
    mergeLotCount: row.mergeLotCount,
    canLaunchJob: row.canLaunchJob,
    blockReason: typeof blockReason === 'string' && blockReason.trim() ? blockReason : undefined,
    annotationsNewerThanChainCount:
      typeof annotationsNewerThanChainCount === 'number' && annotationsNewerThanChainCount >= 0
        ? annotationsNewerThanChainCount
        : undefined,
  };
}

/** CTA Générer : tous les lots affichés doivent avoir un index fusionnable (pas de missing_index / will_be_masked). */
export function u3141CanLaunchAnnotatedLotChainMergeFromDryRun(
  summary: U3141AnnotatedLotChainIndexMergeDryRunSummary | undefined,
): boolean {
  if (!summary?.canLaunchJob) return false;
  if (summary.readyCount <= 0) return false;
  if (summary.missingIndexCount > 0 || summary.maskedCount > 0) return false;
  return true;
}

/** Affichage UI : pas d'énumération de lots dans le bandeau rouge (détail dans la grille). */
export function u3141DisplayAnnotatedMergeBlockReason(blockReason: string | undefined): string | undefined {
  const trimmed = blockReason?.trim();
  if (!trimmed) return undefined;
  return trimmed
    .replace(/\s*\(lots masqués\s*:[^)]+\)/gi, '')
    .replace(/ suivants\s*:[^.]*\./gi, '.')
    .trim();
}

/** Extrait la réponse dry-run (enveloppe `{ result }` ou corps plat). */
export function u3141ParseAnnotatedLotChainIndexMergeDryRunResponse(
  data: unknown,
): U3141AnnotatedLotChainIndexMergeDryRunResponse | null {
  if (!data || typeof data !== 'object') return null;
  const envelope = data as { result?: unknown };
  const raw = envelope.result && typeof envelope.result === 'object' ? envelope.result : data;
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const masterLotChainId = row.masterLotChainId;
  const anchorChildLotChainId = row.anchorChildLotChainId;
  const lotsRaw = row.lots;
  const summary = parseSummary(row.summary);
  if (
    typeof masterLotChainId !== 'string' ||
    typeof anchorChildLotChainId !== 'string' ||
    !Array.isArray(lotsRaw) ||
    !summary
  ) {
    return null;
  }
  const lots: U3141AnnotatedLotChainIndexMergeDryRunLot[] = [];
  for (const entry of lotsRaw) {
    if (!entry || typeof entry !== 'object') continue;
    const lot = entry as Record<string, unknown>;
    if (
      typeof lot.childLotChainId !== 'string' ||
      typeof lot.lotDetailId !== 'string' ||
      typeof lot.lotLabel !== 'string' ||
      typeof lot.status !== 'string' ||
      !isDryRunLotStatus(lot.status)
    ) {
      continue;
    }
    const indexDocumentId = lot.indexDocumentId;
    const annotationsNewerThanChain = lot.annotationsNewerThanChain;
    lots.push({
      childLotChainId: lot.childLotChainId,
      lotDetailId: lot.lotDetailId,
      lotLabel: lot.lotLabel,
      status: lot.status,
      indexDocumentId: typeof indexDocumentId === 'string' ? indexDocumentId : undefined,
      annotationsNewerThanChain:
        typeof annotationsNewerThanChain === 'boolean' ? annotationsNewerThanChain : undefined,
    });
  }
  return { masterLotChainId, anchorChildLotChainId, lots, summary };
}
